import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { message } from 'antd';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import PasswordChecklist from 'react-password-checklist';
import { Lock, User } from 'lucide-react';

import UPDATE_USER from '../../../.././core/GraphQl/Mutations/UPDATE_USER';
import AUTHENTICATED_USER from '../../../../core/GraphQl/Mutations/AUTHENTICATED_USER';
import { getRolesFeatures } from '../../../../core/utils/dataManipulating/organization';
import { Button } from '@/components/ui/button';
import AuthShell from './AuthShell';
import Field from './Field';
import ToggleVisibility from './ToggleVisibility';

const USERNAME_PATTERN = /^[A-Za-z0-9]+$/;

const NewPasswordForm = (props) => {
  const initialUsername =
    (props.location && props.location.state && props.location.state.username) || '';
  const initialPassword =
    (props.location && props.location.state && props.location.state.password) || '';

  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState(initialPassword);
  const [newPassword, setNewPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const next = {};
    if (!username) next.username = 'Username is required';
    else if (!USERNAME_PATTERN.test(username))
      next.username = 'User name must be all one word and not contain spaces';
    if (!password) next.password = 'Password is required';
    if (!newPassword) next.newpassword = 'New password is required';
    if (!verifyPassword) next.verifypassword = 'Verify password is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const loginUser = async (user) => {
    Auth.signOut();
    message.success('Password changed. Please log in!');
    props.history.push('/signin');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      await Auth.signOut();
      if (!Auth || typeof Auth.signIn !== 'function') {
        throw new Error(
          'No Auth module found, please ensure @aws-amplify/auth is imported'
        );
      }
      Auth.signIn(username, password)
        .then((u) => {
          if (
            u.challengeName === 'SMS_MFA' ||
            u.challengeName === 'SOFTWARE_TOKEN_MFA'
          ) {
            props.history.push({
              pathname: '/mfa',
              state: { username, password },
            });
          } else if (u.challengeName === 'NEW_PASSWORD_REQUIRED') {
            if (newPassword !== verifyPassword) {
              message.warning('Passwords do not match');
              setLoading(false);
            } else {
              Auth.completeNewPassword(u, newPassword)
                .then((userWithNewPass) => loginUser(userWithNewPass))
                .catch(() => setLoading(false));
            }
          } else if (u.challengeName === 'MFA_SETUP') {
            props.history.push('/setup');
          } else {
            loginUser(u);
          }
        })
        .catch((err) => {
          console.log(err);
          if (err.code === 'UserNotConfirmedException') {
            message.warning('Sign up confirmation not completed!');
          } else if (err.code === 'PasswordResetRequiredException') {
            props.history.push('/mfa');
          }
          setLoading(false);
        });
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  return (
    <AuthShell
      title="Set new password"
      subtitle="Choose a new password to finish signing in."
      onLogoClick={() => props.history.push('/')}
    >
      <form onSubmit={onSubmit} noValidate>
        <div className="space-y-4">
          <Field
            id="new-username"
            label="Username"
            icon={User}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={errors.username}
            autoComplete="username"
            placeholder="yourusername"
          />

          <Field
            id="new-password"
            label="Current password"
            icon={Lock}
            type={showCurrent ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            autoComplete="current-password"
            placeholder="••••••••"
            rightAdornment={
              <ToggleVisibility shown={showCurrent} onClick={() => setShowCurrent((s) => !s)} />
            }
          />

          <Field
            id="new-newpassword"
            label="New password"
            icon={Lock}
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={errors.newpassword}
            autoComplete="new-password"
            placeholder="••••••••"
            rightAdornment={
              <ToggleVisibility shown={showNew} onClick={() => setShowNew((s) => !s)} />
            }
          />

          <Field
            id="new-verifypassword"
            label="Verify new password"
            icon={Lock}
            type={showVerify ? 'text' : 'password'}
            value={verifyPassword}
            onChange={(e) => setVerifyPassword(e.target.value)}
            error={errors.verifypassword}
            autoComplete="new-password"
            placeholder="••••••••"
            rightAdornment={
              <ToggleVisibility shown={showVerify} onClick={() => setShowVerify((s) => !s)} />
            }
          />
        </div>

        <div className="checklistContainer mt-4 text-[13px]">
          <PasswordChecklist
            rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase', 'match']}
            minLength={8}
            value={newPassword}
            valueAgain={verifyPassword}
            iconSize={13}
            invalidColor={'#ff0000'}
            onChange={() => {}}
          />
          <style>
            {`
              .checklistContainer li.invalid { align-items: center !important }
              .checklistContainer li.valid   { align-items: center !important }
            `}
          </style>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-6 w-full bg-[#1890ff] hover:bg-[#096dd9]"
        >
          {loading ? 'Working…' : 'Change password'}
        </Button>
      </form>
    </AuthShell>
  );
};

export default compose(
  graphql(UPDATE_USER, { name: 'updateUser' }),
  graphql(AUTHENTICATED_USER, { name: 'authenticatedUser' })
)(NewPasswordForm);
